/**
 * Attaching a receipt to a card charge.
 *
 * Suggestions come from `lib/banking/match.ts` and are only offered when the amount agrees exactly - a
 * wrong automatic match is worse than none. Anything else is found by searching, and a row that
 * genuinely has no receipt (bank fees, SVS, a private withdrawal, a transfer) is booked without
 * one (`BookingForm`).
 */
import { Check, Link2, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BelegNumber, KindBadge } from "@/components/documents/document-badges";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { BookingForm } from "@/components/banking/booking-form";
import { useLinkDocument } from "@/hooks/use-banking";
import type { Transaction } from "@/lib/api/banking";
import type { DocumentRecord } from "@/lib/api/documents";
import { formatDate, formatMoney } from "@/lib/utils/format";
import { scoreMatch, type Match } from "@/lib/banking/match";
import { transactionDate } from "@/lib/banking/period-close";
import { cn } from "@/lib/utils/cn";

export function MatchDialog({
  transaction,
  documents,
  onOpenChange,
}: {
  transaction: Transaction | null;
  documents: DocumentRecord[];
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const link = useLinkDocument();
  // Set while the row is being booked without a receipt instead.
  const [booking, setBooking] = useState(false);

  const suggestions = useMemo(() => {
    if (!transaction) return new Map<string, Match>();
    const map = new Map<string, Match>();
    for (const document of documents) {
      // Dated by the purchase, like the receipt; the tip counts towards what the card was charged.
      const match = scoreMatch(
        { ...transaction, bookingDate: transactionDate(transaction) },
        {
          id: document.id,
          kind: document.kind,
          grossTotal: document.grossTotal,
          tip: document.tip,
          issueDate: document.issueDate,
          paidDate: document.paidDate,
          partnerName: document.partnerName,
        },
      );
      if (match) map.set(document.id, match);
    }
    return map;
  }, [transaction, documents]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? documents.filter((document) =>
          [document.partnerName, document.docNumber, document.invoiceNumber]
            .filter(Boolean)
            .some((value) => value!.toLowerCase().includes(term)),
        )
      : documents.filter((document) => suggestions.has(document.id));
    return [...filtered].sort(
      (a, b) => (suggestions.get(b.id)?.score ?? 0) - (suggestions.get(a.id)?.score ?? 0),
    );
  }, [documents, search, suggestions]);

  async function attach(documentId: string) {
    if (!transaction) return;
    await link.mutateAsync({ transactionId: transaction.id, documentId });
    toast.success(t("banking.linked"));
    onOpenChange(false);
  }

  return (
    // A fresh dialog per row: reopening the same one while it still animates closed hands focus
    // back to the old trigger, which Radix takes as a click outside and closes the new one.
    <Dialog
      key={transaction?.id ?? "closed"}
      open={transaction !== null}
      onOpenChange={(open) => {
        if (!open) setBooking(false);
        onOpenChange(open);
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{booking ? t("booking.title") : t("banking.matchTitle")}</DialogTitle>
          <DialogDescription>
            {transaction && (
              <>
                {formatDate(transaction.bookingDate)} · {transaction.counterparty} ·{" "}
                <span className="font-medium text-foreground num">
                  {formatMoney(transaction.amount, transaction.currency)}
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {booking && transaction ? (
          <BookingForm
            transaction={transaction}
            onBack={() => setBooking(false)}
            onDone={() => {
              setBooking(false);
              onOpenChange(false);
            }}
          />
        ) : (
          <>
            <div className="relative">
              <Search className="absolute top-2.5 left-2.5 size-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder={t("banking.searchDocuments")}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                data-testid="match-search"
              />
            </div>

            <div className="max-h-80 space-y-1 overflow-y-auto">
              {visible.length === 0 && (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {search ? t("banking.noResults") : t("banking.noSuggestions")}
                </p>
              )}
              {visible.map((document) => {
                const match = suggestions.get(document.id);
                return (
                  <button
                    key={document.id}
                    type="button"
                    onClick={() => void attach(document.id)}
                    disabled={link.isPending}
                    data-testid="match-candidate"
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors hover:bg-accent/60",
                      match ? "border-success/40" : "border-border",
                    )}
                  >
                    <KindBadge kind={document.kind} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {document.partnerName || t("document.noPartner")}
                      </p>
                      <p className="flex items-center gap-2 text-xs text-muted-foreground">
                        <BelegNumber value={document.docNumber} />
                        {formatDate(document.issueDate)}
                      </p>
                    </div>
                    <span className="text-sm num">
                      {formatMoney(document.grossTotal, document.currency)}
                    </span>
                    {match && (
                      <span
                        className="flex items-center gap-1 text-xs text-success"
                        title={match.reasons
                          .map((reason) => t(`banking.reason.${reason}`))
                          .join(", ")}
                      >
                        <Check className="size-3.5" />
                        {Math.round(match.score * 100)}%
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <DialogFooter className="sm:justify-between">
              <Button
                variant="outline"
                disabled={!transaction}
                data-testid="mark-no-receipt"
                onClick={() => setBooking(true)}
              >
                <Link2 className="rotate-45" /> {t("banking.noReceiptNeeded")}
              </Button>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
